import { Link } from 'react-router-dom'
import { createLocalizedPath } from '../utils/i18n'

export function About() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <nav className="bg-white border-b border-gray-200" aria-label="Breadcrumb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex" itemScope itemType="https://schema.org/BreadcrumbList">
            <ol className="flex items-center space-x-4 py-4">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link 
                  to={createLocalizedPath('/')} 
                  className="text-gray-400 hover:text-gray-500"
                  itemProp="item"
                >
                  <span itemProp="name">Home</span>
                </Link>
                <meta itemProp="position" content="1" />
              </li>
              <li className="flex">
                <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span className="text-gray-500" itemProp="name">About & Terms</span>
                <meta itemProp="position" content="2" />
              </li>
            </ol>
          </div>
        </div>
      </nav>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to={createLocalizedPath('/')} className="text-2xl font-bold text-primary-600 hover:text-primary-700">
                BlinkPDF
              </Link>
              <span className="ml-2 text-sm text-gray-500">PDF Tools in Your Browser</span>
            </div>
            <nav>
              <Link to={createLocalizedPath('/')} className="text-gray-600 hover:text-gray-900 font-medium">
                ← Back to App
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">About BlinkPDF</h1>
          
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What is BlinkPDF?</h2>
              <p className="text-gray-700 mb-4">
                BlinkPDF is a free, browser-based PDF processing tool that allows you to select specific pages 
                from PDF documents and create new PDFs with only the pages you need. All processing happens 
                locally in your browser, ensuring your documents remain private and secure.
              </p>
              <p className="text-gray-700">
                No uploads, no registration, no installation required. Just open your browser and start 
                working with your PDFs immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Features</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Select and extract specific pages from PDF documents</li>
                <li>Preview pages with thumbnail view</li>
                <li>100% browser-based processing - no server uploads</li>
                <li>Completely private - files never leave your device</li>
                <li>Fast and responsive interface</li>
                <li>Works on desktop and mobile browsers</li>
                <li>No installation or registration required</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy & Security</h2>
              <p className="text-gray-700 mb-4">
                Your privacy is our top priority. BlinkPDF processes all PDF files entirely within your browser 
                using JavaScript and WebAssembly. This means:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
                <li>Your files are never uploaded to any server</li>
                <li>No data is stored or transmitted over the internet</li>
                <li>All processing happens locally on your device</li>
                <li>We don't track, store, or analyze your documents</li>
                <li>Works completely offline once the page is loaded</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Terms of Use & Disclaimer</h2>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">Free to Use</h3>
                <p className="text-yellow-700">
                  BlinkPDF is provided free of charge for personal and commercial use. You are welcome to use 
                  this tool for any legitimate purpose.
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-red-800 mb-3">No Warranties or Guarantees</h3>
                <p className="text-red-700 mb-3">
                  <strong>IMPORTANT:</strong> BlinkPDF is provided "as is" without any warranties or guarantees 
                  of any kind, either express or implied. We make no representations about:
                </p>
                <ul className="list-disc list-inside text-red-700 space-y-1">
                  <li>The accuracy, reliability, or completeness of the service</li>
                  <li>The quality or integrity of processed PDF files</li>
                  <li>Continuous availability or uptime of the service</li>
                  <li>Compatibility with all PDF formats or browsers</li>
                </ul>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Limitation of Liability</h3>
                <p className="text-gray-700 mb-3">
                  In no event shall the creators or contributors of BlinkPDF be liable for any direct, indirect, 
                  incidental, special, consequential, or punitive damages, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mb-3">
                  <li>Loss of data or corrupted files</li>
                  <li>Business interruption or lost profits</li>
                  <li>Any damages arising from the use or inability to use this service</li>
                </ul>
                <p className="text-gray-700 text-sm">
                  <strong>This site always saves the result in a new file and never changes the original</strong>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Technical Information</h2>
              <p className="text-gray-700 mb-4">
                BlinkPDF is built using modern web technologies:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>React and TypeScript for the user interface</li>
                <li>PDF.js for PDF processing and rendering</li>
                <li>Canvas API for thumbnail generation</li>
                <li>Modern browser APIs for file handling</li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p>&copy; 2025 BlinkPDF. Built for privacy and performance.</p>
            <p className="mt-2 text-sm">
              <Link to={createLocalizedPath('/')} className="text-primary-600 hover:text-primary-700">
                Back to PDF Tools
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
